/**
 * Pure half of the per-user musync session: no store, no axios, no window.
 *
 * Every Feishin user signs in to musync as themselves by handing musync the
 * Subsonic token credential Feishin already holds for Navidrome
 * (`u=<user>&s=<salt>&t=<token>`); musync validates it against Navidrome and
 * answers with its own session token. The decisions that do not need I/O (what
 * the cache is keyed by, which credentials can be exchanged at all, when a
 * cached token is still usable, which entry goes stale when the Feishin login
 * changes) live here so `tests/playlist-import-flow.test.mts` can pin them
 * without a browser.
 */

/** What musync handed back for one exchange, as cached in localStorage. */
export interface CachedMusyncSession {
    /**
     * `me.capabilities` from the exchange (e.g. `wants:write`, `download`);
     * null when unknown (an entry cached before capabilities were stored).
     */
    capabilities?: null | string[];
    /** ISO timestamp; null when musync did not say. */
    expires_at: null | string;
    token: string;
}

export const MUSYNC_CAPABILITY_DOWNLOAD = 'download';
export const MUSYNC_CAPABILITY_WANTS_WRITE = 'wants:write';

/** What the signed-in musync role may do, as far as the UI needs to know. */
export interface MusyncPermissions {
    /** `download`: start Soulseek searches and downloads, see Feishin downloads. */
    canDownload: boolean;
    /** `wants:write`: follow, scan, publish, change playlists, approve fallbacks. */
    canWrite: boolean;
    /** False while the capabilities are not known yet (musync still decides). */
    known: boolean;
}

/** The capability list of `POST /auth/navidrome`'s `me`, or null when absent. */
export const capabilitiesFromMe = (me: unknown): null | string[] => {
    if (!me || typeof me !== 'object') return null;
    const list = (me as { capabilities?: unknown }).capabilities;
    if (!Array.isArray(list)) return null;
    return list.filter((item): item is string => typeof item === 'string');
};

/**
 * Unknown capabilities allow everything: the backend enforces roles anyway,
 * and hiding actions from a curator because of a stale cache entry is worse
 * than showing a viewer a 403 once.
 */
export const musyncPermissions = (
    capabilities: null | readonly string[] | undefined,
): MusyncPermissions => {
    if (!Array.isArray(capabilities)) return { canDownload: true, canWrite: true, known: false };
    return {
        canDownload: capabilities.includes(MUSYNC_CAPABILITY_DOWNLOAD),
        canWrite: capabilities.includes(MUSYNC_CAPABILITY_WANTS_WRITE),
        known: true,
    };
};

/** FastAPI's `detail` when it is a plain message (not a validation list). */
export const responseDetail = (data: unknown): null | string => {
    if (!data || typeof data !== 'object') return null;
    const detail = (data as { detail?: unknown }).detail;
    return typeof detail === 'string' && detail.trim() ? detail.trim() : null;
};

export const MUSYNC_SIGN_IN_FAILED_MESSAGE =
    'Could not sign in to musync with your Navidrome account';
export const MUSYNC_UNREACHABLE_MESSAGE =
    'Could not reach musync. Check that the backend is online and this Feishin origin is allowed.';

/**
 * What to tell the user when the session exchange failed. musync's own
 * reason ("Untrusted or missing browser origin", "This account is disabled")
 * is the actionable part, so it is shown whenever musync gave one; the
 * generic sign-in message is only for failures without a reason.
 */
export const musyncSessionErrorText = ({
    detail,
    status,
}: {
    detail: null | string | undefined;
    status: null | number | undefined;
}): string => {
    if (detail) return `${MUSYNC_SIGN_IN_FAILED_MESSAGE}: ${detail}`;
    if (status === 503) return 'musync cannot reach Navidrome to sign you in. Try again shortly.';
    if (status === null || status === undefined) return MUSYNC_UNREACHABLE_MESSAGE;
    return MUSYNC_SIGN_IN_FAILED_MESSAGE;
};

export interface MusyncSessionIdentity {
    cacheKey: string;
    credential: string;
}

/** The subset of a Feishin server entry the session cares about. */
export interface MusyncSessionServer {
    credential?: null | string;
    id: string;
    type: string;
    username: string;
}

/** Minimal Storage surface, so tests can pass a Map-backed fake. */
export interface MusyncSessionStorage {
    getItem: (key: string) => null | string;
    removeItem: (key: string) => void;
    setItem: (key: string, value: string) => void;
}

export interface SubsonicTokenCredential {
    s: string;
    t: string;
    u: string;
}

export const MUSYNC_SESSION_STORAGE_PREFIX = 'musync_session:';

/** Treat a token as expired this long before musync would, to absorb clock skew. */
export const MUSYNC_SESSION_EXPIRY_MARGIN_MS = 60_000;

/** One musync session per Feishin server entry and Navidrome user. */
export const musyncSessionCacheKey = (serverId: string, username: string): string =>
    `${serverId}:${username}`;

export const musyncSessionStorageKey = (cacheKey: string): string =>
    `${MUSYNC_SESSION_STORAGE_PREFIX}${cacheKey}`;

const decodePart = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

/**
 * Parse Feishin's Navidrome credential. Only the salted token form can be
 * exchanged: a legacy `p=` (plain password) credential is refused rather than
 * shipped to another backend.
 */
export const parseSubsonicCredential = (
    credential: null | string | undefined,
): null | SubsonicTokenCredential => {
    if (!credential) return null;
    const parts: Record<string, string> = {};
    for (const pair of credential.split('&')) {
        const index = pair.indexOf('=');
        if (index <= 0) continue;
        parts[pair.slice(0, index)] = decodePart(pair.slice(index + 1));
    }
    if (!parts.u || !parts.s || !parts.t || 'p' in parts) return null;
    return { s: parts.s, t: parts.t, u: parts.u };
};

/** Only a Navidrome login can be exchanged for a musync session. */
export const isNavidromeServer = (server: null | Partial<MusyncSessionServer> | undefined) =>
    Boolean(server && server.type === 'navidrome');

/** The cache key and credential to exchange, or null when this login cannot sign in. */
export const musyncSessionIdentity = (
    server: MusyncSessionServer | null | undefined,
): MusyncSessionIdentity | null => {
    if (!server || !isNavidromeServer(server) || !server.id || !server.username) return null;
    if (!parseSubsonicCredential(server.credential)) return null;
    return {
        cacheKey: musyncSessionCacheKey(server.id, server.username),
        credential: server.credential as string,
    };
};

/** A cached token is usable until shortly before its expiry; no expiry means usable. */
export const isCachedSessionUsable = (
    session: CachedMusyncSession | null | undefined,
    now: number,
    marginMs = MUSYNC_SESSION_EXPIRY_MARGIN_MS,
): session is CachedMusyncSession => {
    if (!session || typeof session.token !== 'string' || !session.token) return false;
    if (!session.expires_at) return true;
    const expires = Date.parse(session.expires_at);
    if (!Number.isFinite(expires)) return true;
    return expires - marginMs > now;
};

export const readCachedSession = (
    storage: MusyncSessionStorage,
    cacheKey: string,
    now: number,
): CachedMusyncSession | null => {
    let parsed: unknown;
    try {
        const raw = storage.getItem(musyncSessionStorageKey(cacheKey));
        if (!raw) return null;
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    const session = parsed as CachedMusyncSession | null;
    if (!isCachedSessionUsable(session, now)) return null;
    return {
        capabilities: capabilitiesFromMe(session),
        expires_at: session.expires_at ?? null,
        token: session.token,
    };
};

export const writeCachedSession = (
    storage: MusyncSessionStorage,
    cacheKey: string,
    session: CachedMusyncSession,
): void => {
    try {
        storage.setItem(
            musyncSessionStorageKey(cacheKey),
            JSON.stringify({
                capabilities: session.capabilities ?? null,
                expires_at: session.expires_at ?? null,
                token: session.token,
            }),
        );
    } catch {
        // Storage full or blocked: the token still works for this page load.
    }
};

export const clearCachedSession = (storage: MusyncSessionStorage, cacheKey: string): void => {
    try {
        storage.removeItem(musyncSessionStorageKey(cacheKey));
    } catch {
        // Nothing cached is the state we wanted anyway.
    }
};

/**
 * Which cached session, if any, a Feishin login change makes stale: the
 * previous one, whenever the server, the user or the credential moved
 * (switching servers, switching users, re-login with a new salt, logout).
 * Hydrating from nothing drops nothing.
 */
export const staleMusyncSessionKey = (
    previous: MusyncSessionServer | null | undefined,
    next: MusyncSessionServer | null | undefined,
): null | string => {
    if (!previous || !previous.id || !previous.username) return null;
    const previousKey = musyncSessionCacheKey(previous.id, previous.username);
    if (!next || !next.id || !next.username) return previousKey;
    const nextKey = musyncSessionCacheKey(next.id, next.username);
    if (nextKey !== previousKey) return previousKey;
    if ((previous.credential || '') !== (next.credential || '')) return previousKey;
    if (previous.type !== next.type) return previousKey;
    return null;
};

// --- Session manager -------------------------------------------------------------

/** Raised when there is no usable musync session for the current Feishin login. */
export class MusyncSessionError extends Error {
    /** musync's own reason (`detail`), e.g. "Untrusted or missing browser origin". */
    readonly detail: null | string;
    /** The underlying transport error, if any. */
    readonly original: unknown;
    /** HTTP status of the failed exchange, when musync answered at all. */
    readonly status: null | number;

    constructor(
        message: string,
        status: null | number = null,
        original: unknown = null,
        detail: null | string = null,
    ) {
        super(message);
        this.name = 'MusyncSessionError';
        this.status = status;
        this.original = original;
        this.detail = detail;
    }
}

export const MUSYNC_LOGIN_CHANGED_MESSAGE =
    'Your Feishin login changed, so a musync request of the previous login was cancelled.';

/** The request belongs to a login that is no longer the current one. */
export const loginChangedError = (): MusyncSessionError =>
    new MusyncSessionError(MUSYNC_LOGIN_CHANGED_MESSAGE, 401);

export const isMusyncLoginChangedError = (error: unknown): boolean =>
    error instanceof MusyncSessionError && error.message === MUSYNC_LOGIN_CHANGED_MESSAGE;

/**
 * One sign-in of one Feishin user. `generation` changes on every login change
 * (other user, new credential, logout and back in), so two sign-ins of the
 * same user are told apart even when the user name, and even the
 * credential, are the same.
 */
export interface MusyncLoginPin {
    cacheKey: string;
    credential: string;
    generation: number;
}

export interface MusyncSessionManager {
    /** Forget the cached token of a login (musync rejected it), if it is still current. */
    drop: (pin: MusyncLoginPin) => void;
    /** The Feishin login changed: forget `cacheKey` and end the current generation. */
    forget: (cacheKey: string) => void;
    getSession: (request?: MusyncSessionRequest) => Promise<CachedMusyncSession>;
    /** Is this sign-in still the current one? */
    isCurrent: (pin: MusyncLoginPin) => boolean;
    /** The current sign-in, or null without a musync-capable login. */
    pin: () => MusyncLoginPin | null;
}

export interface MusyncSessionManagerDeps {
    /** The Feishin login right now (read again after every await). */
    currentIdentity: () => MusyncSessionIdentity | null;
    /** `POST /auth/navidrome` for exactly this identity. */
    exchange: (identity: MusyncSessionIdentity) => Promise<CachedMusyncSession>;
    now?: () => number;
    /** A fresh session was stored for the (still current) login. */
    onStored?: (cacheKey: string, session: CachedMusyncSession) => void;
    storage: () => MusyncSessionStorage;
}

export interface MusyncSessionRequest {
    force?: boolean;
    /**
     * The sign-in the caller acts for. When it is not the current one (before
     * or after the exchange), the call fails instead of handing out a token of
     * another login. Defaults to the current sign-in.
     */
    pin?: MusyncLoginPin;
}

/**
 * Hands out the session of the sign-in a caller acts for, and only while that
 * sign-in is still the current one. A token obtained for one sign-in is never
 * cached or returned once Feishin moved on, whether to another user or to a
 * new sign-in of the same user, so a request started under one login cannot
 * go out, or be retried, under another.
 */
export const createMusyncSessionManager = (
    deps: MusyncSessionManagerDeps,
): MusyncSessionManager => {
    const now = deps.now ?? Date.now;
    let generation = 0;
    /** The identity last seen, to notice a change even if `forget` was missed. */
    let seen: MusyncSessionIdentity | null = null;
    /** One exchange per sign-in at a time: concurrent requests share it. */
    const inFlight = new Map<string, Promise<CachedMusyncSession>>();

    const pin = (): MusyncLoginPin | null => {
        const identity = deps.currentIdentity();
        if (
            seen &&
            (!identity ||
                identity.cacheKey !== seen.cacheKey ||
                identity.credential !== seen.credential)
        ) {
            generation += 1;
            inFlight.clear();
        }
        seen = identity;
        if (!identity) return null;
        return { cacheKey: identity.cacheKey, credential: identity.credential, generation };
    };

    const isCurrent = (target: MusyncLoginPin): boolean => {
        const current = pin();
        return (
            current !== null &&
            current.generation === target.generation &&
            current.cacheKey === target.cacheKey &&
            current.credential === target.credential
        );
    };

    const getSession = async ({
        force = false,
        pin: requested,
    }: MusyncSessionRequest = {}): Promise<CachedMusyncSession> => {
        const current = pin();
        if (!current) {
            throw new MusyncSessionError('No Navidrome login to sign in to musync with', 401);
        }
        const target = requested ?? current;
        if (!isCurrent(target)) throw loginChangedError();

        if (!force) {
            const cached = readCachedSession(deps.storage(), target.cacheKey, now());
            if (cached) return cached;
        }

        const flightKey = `${target.generation}:${target.cacheKey}`;
        let request = inFlight.get(flightKey);
        if (!request) {
            request = deps
                .exchange({ cacheKey: target.cacheKey, credential: target.credential })
                .then((session) => {
                    if (isCurrent(target)) {
                        writeCachedSession(deps.storage(), target.cacheKey, session);
                        deps.onStored?.(target.cacheKey, session);
                    }
                    return session;
                });
            const shared = request;
            inFlight.set(flightKey, shared);
            void shared
                .catch(() => undefined)
                .finally(() => {
                    if (inFlight.get(flightKey) === shared) inFlight.delete(flightKey);
                });
        }

        const session = await request;
        // The login may have changed while the exchange was in flight.
        if (!isCurrent(target)) throw loginChangedError();
        return session;
    };

    return {
        drop: (target) => {
            if (isCurrent(target)) clearCachedSession(deps.storage(), target.cacheKey);
        },
        forget: (cacheKey) => {
            clearCachedSession(deps.storage(), cacheKey);
            generation += 1;
            seen = deps.currentIdentity();
            inFlight.clear();
        },
        getSession,
        isCurrent,
        pin,
    };
};

// --- Request binding ---------------------------------------------------------------

/**
 * Options every musync mutation is created with. By default TanStack parks a
 * mutation asked for while the browser is offline and only runs it once the
 * browser is back online, by which time another Feishin login may be current:
 * its request would then be pinned to, and sent as, that other login.
 * `networkMode: 'always'` prevents offline parking, but TanStack still awaits
 * before mutationFn. useMusyncMutation separately pins the login synchronously
 * in mutate/mutateAsync, before any of those awaits.
 */
export const MUSYNC_MUTATION_OPTIONS = { networkMode: 'always' } as const;

/** The parts of an axios request config the auth hooks touch. */
export interface MusyncBoundRequest {
    _musyncPin?: MusyncLoginPin | null;
    _musyncRetried?: boolean;
    headers: { set: (name: string, value: string) => unknown };
}

/**
 * Attach the bearer token of the sign-in the request was started under. The
 * first call pins that sign-in on the request; a retry of the same request
 * keeps it, so the request fails rather than run under whoever signed in since.
 */
export const authorizeMusyncRequest = async <T extends MusyncBoundRequest>(
    manager: MusyncSessionManager,
    config: T,
): Promise<T> => {
    const pinned = config._musyncPin === undefined ? manager.pin() : config._musyncPin;
    if (!pinned) {
        throw new MusyncSessionError('No Navidrome login to sign in to musync with', 401);
    }
    config._musyncPin = pinned;
    const session = await manager.getSession({ pin: pinned });
    config.headers.set('Authorization', `Bearer ${session.token}`);
    return config;
};

/**
 * A response (or error) that arrives after its sign-in ended is not handed to
 * the caller: it would otherwise land in the next login's query cache, or be
 * shown to the next user. Throws the login-changed error in that case.
 */
export const assertMusyncRequestCurrent = (
    manager: MusyncSessionManager,
    config: MusyncBoundRequest | undefined,
): void => {
    const pinned = config?._musyncPin;
    if (pinned && !manager.isCurrent(pinned)) throw loginChangedError();
};

/**
 * After a 401: drop the pinned sign-in's token, sign that same sign-in in
 * again and resend once. Throws `original` when there is nothing to retry,
 * and a login-changed error when the login moved on, so a write started under
 * one login is never replayed under another.
 */
export const retryUnauthorizedMusyncRequest = async <T extends MusyncBoundRequest, R>(
    manager: MusyncSessionManager,
    config: T,
    original: unknown,
    resend: (config: T) => Promise<R>,
): Promise<R> => {
    const pinned = config._musyncPin;
    if (config._musyncRetried || !pinned) throw original;
    if (!manager.isCurrent(pinned)) throw loginChangedError();
    config._musyncRetried = true;
    manager.drop(pinned);
    await manager.getSession({ force: true, pin: pinned });
    return resend(config);
};
